// Interfaces

// NPM
import moment from "moment";
import Axios, { AxiosResponse } from "axios";

// Config
import {
	LOCAL_HOST,
	PRACTIQ_API_ENDPOINT,
	PRACTIQ_API_KEY,
	QV_MEDICAL_WEBSITE_API_KEY,
	QV_MEDICAL_WEBSITE_ENDPOINT,
} from "../utility/config";

// Interfaces
import ITelehealthDay from "../interfaces/ITelehealthDay";
import IAppointmentTypeProviderDto from "../interfaces/IAppointmentTypeProviderDto";
import IDoctor from "../interfaces/IDoctor";

// Enum
import DeliveryMethod from "../enums/DeliveryMethod";

//Type
import DeliveryMethodType from "../types/DeliveryMethodType";

export default class Doctor {
	private static readonly apiHost = process.env.NODE_ENV === "production" ? PRACTIQ_API_ENDPOINT : LOCAL_HOST;

	public static getDoctorByBPId(id: string, doctors: IDoctor[]): IDoctor {
		return doctors.filter((doc: IDoctor) => doc.ClinixId === id)[0];
	}

	public static async getAll(): Promise<AxiosResponse<IDoctor[]>> {
		return await Axios.request<IDoctor[]>({
			url: QV_MEDICAL_WEBSITE_ENDPOINT + "/practitioner",
			method: "GET",
			headers: {
				"X-Api-Key": QV_MEDICAL_WEBSITE_API_KEY,
			},
		});
	}

	public static onlyProvidesTelehealthOn(
		dateAt: string,
		telehealthSchedule: ITelehealthDay[]
	): boolean {
		const apptDay: moment.Moment = moment(dateAt);

		return telehealthSchedule.filter(
			(item: ITelehealthDay) => item.value === apptDay.isoWeekday()
		)[0].checked;
	}

	public static async getProvidersOfAppointmentType(
		id: number
	): Promise<AxiosResponse<IAppointmentTypeProviderDto[]>> {
		return await Axios.request<IAppointmentTypeProviderDto[]>({
			url:
			this.apiHost +
				"/healthprofessional/can-provide-appointment-type/" +
				id,
			method: "GET",
			headers: {
				"X-Api-Key": PRACTIQ_API_KEY
			}
		});
	}

	public static generateFullName(
		doctorData: IDoctor,
		withTitle?: boolean
	): string {
		const fullName: string = `${doctorData.Firstname} ${doctorData.Lastname}`;
		return withTitle ? `${doctorData.Title} ` + fullName : fullName;
	}

	public static provides(
		method: "telehealthAlso" | "onsiteAlso",
		schedule: ITelehealthDay[],
		dayOfWeek: number
	): boolean {
		if (method === "onsiteAlso") {
			return (
				schedule.filter(
					(day: ITelehealthDay) =>
						!day.checked && dayOfWeek === day.value
				).length > 0
			);
		} else if (method === "telehealthAlso") {
			return (
				schedule.filter((day: ITelehealthDay) => day.checked).length > 0
			);
		}

		return false;
	}

	public static canProvideTelehealth(
		providedDeliveryMethod: DeliveryMethodType,
		telehealthOnlySchedule: ITelehealthDay[],
		dayOfWeek: number
	): boolean {
		return (
			(providedDeliveryMethod === DeliveryMethod.OnSiteAndTelehealth ||
				providedDeliveryMethod === DeliveryMethod.TelehealthOnly) &&
			Doctor.provides("telehealthAlso", telehealthOnlySchedule, dayOfWeek)
		);
	}

	public static isAvailableForOnsiteAppt(
		providedDeliveryMethod: DeliveryMethodType,
		telehealthOnlySchedule: ITelehealthDay[],
		dayOfWeek: number
	): boolean {
		return (
			(providedDeliveryMethod === DeliveryMethod.OnSiteAndTelehealth ||
				providedDeliveryMethod === DeliveryMethod.OnsiteOnly) &&
			Doctor.provides("onsiteAlso", telehealthOnlySchedule, dayOfWeek)
		);
	}

	public static getProviderOverrides(
		providerList: IAppointmentTypeProviderDto[],
		bpId: number
	): IAppointmentTypeProviderDto | false {
		const override = providerList.filter(
			(provider: IAppointmentTypeProviderDto) =>
				parseInt(provider.bpId) === bpId
		);

		if (override.length > 0) return override[0];

		return false;
	}

	/**
	 *
	 * @param providerList Deprecated
	 * @param userId
	 */
	public static acceptsNewPatient(
		providerList: IAppointmentTypeProviderDto[],
		userId: number
	): boolean {
		const provider = providerList.filter(
			(provider: IAppointmentTypeProviderDto) =>
				provider.bpId === userId.toString()
		)[0];

		if (provider) {
			return provider.allowNewPatient;
		}

		return false;
	}
}
